import axios, {
  type AxiosInstance,
  type InternalAxiosRequestConfig,
  type AxiosHeaders,
} from "axios";
import ApiEndpoints from "./apiEndPoints";
import { getToken, removeToken } from "../utils/auth";

const httpClient = (
  contentType: string = "application/json"
): AxiosInstance => {
  const instance = axios.create({
    baseURL: ApiEndpoints.baseApiURL,
    withCredentials: true,
    headers: {
      "Content-Type": contentType,
    },
    validateStatus: (status: number) => status <= 500,
  });

  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = getToken();
    const authValue = token ? `Bearer ${token}` : "";

    // Support both AxiosHeaders (v1) and plain object headers
    if ((config.headers as AxiosHeaders)?.set) {
      (config.headers as AxiosHeaders).set("Authorization", authValue);
      (config.headers as AxiosHeaders).set("Content-Type", contentType);
    } else {
      // Fallback for plain object headers
      (config.headers as Record<string, string>) = {
        ...(config.headers as Record<string, string>),
        Authorization: authValue,
        "Content-Type": contentType,
      };
    }

    return config;
  });

  instance.interceptors.response.use(
    (response) => {
      const token = getToken();

      // Check for 401 Unauthorized
      if (token && response.status === 401) {
        // Clear token
        removeToken();

        // Clear any stored user data
        localStorage.clear();
        sessionStorage.clear();

        // Redirect to login page
        window.location.href = "/login";

        return Promise.reject(new Error("Unauthorized - Session expired"));
      }

      return response;
    },
    (error) => {
      // Handle network errors or errors from axios
      if (error.response?.status === 401) {
        removeToken();
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = "/login";
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

export default httpClient;
